/**
 * Etapa 5 — Configuracoes de notificacoes do aplicativo.
 *
 * 3 notificacoes push automaticas enviadas pelo sistema todos os dias:
 *  - 09:00 — Lembrete leve de organizacao (Informativo)
 *  - 17:00 — Lembrete da parcela / verificacao (Alerta)
 *  - 19:00-21:00 — Ultimo lembrete do dia (Importante)
 *
 * Horarios e funcionamento sao fixos. Cliente pode desativar individualmente
 * cada uma e personalizar o texto. Variaveis suportadas: {nome_cliente},
 * {valor_parcela}, {data_vencimento}.
 */

import type { Dispatch, SetStateAction } from "react";
import { InfoCallout, SectionCard } from "../components/SectionCard";
import { Toggle } from "../components/Toggle";
import type { NotificacaoKey, PlanFormState } from "../types";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

interface SlotMeta {
  key: NotificacaoKey;
  horario: string;
  horarioLabel: string;
  titulo: string;
  badge: string;
  badgeTone: "info" | "warning" | "danger";
  descricao: string;
  icon: "sun" | "clock" | "moon";
  variaveis: string[];
  maxLen: number;
}

const SLOTS: SlotMeta[] = [
  {
    key: "organizacao",
    horario: "09:00",
    horarioLabel: "Todos os dias",
    titulo: "Lembrete leve de organizacao",
    badge: "Informativo",
    badgeTone: "info",
    descricao:
      "As 09:00 da manha, enviamos uma mensagem leve e amigavel para todos os clientes. O objetivo e lembrar sobre seus compromissos e manter a organizacao em dia.",
    icon: "sun",
    variaveis: ["{nome_cliente}"],
    maxLen: 160,
  },
  {
    key: "parcela",
    horario: "17:00",
    horarioLabel: "Todos os dias",
    titulo: "Lembrete da parcela (verificacao)",
    badge: "Alerta",
    badgeTone: "warning",
    descricao:
      "As 17:00 da tarde, o sistema faz uma varredura e identifica quem ainda nao realizou o pagamento. Enviamos um lembrete com todos os detalhes: valor, vencimento e data.",
    icon: "clock",
    variaveis: ["{nome_cliente}", "{valor_parcela}", "{data_vencimento}"],
    maxLen: 160,
  },
  {
    key: "ultimo",
    horario: "19:00 - 21:00",
    horarioLabel: "Todos os dias",
    titulo: "Ultimo lembrete do dia (nova verificacao)",
    badge: "Importante",
    badgeTone: "danger",
    descricao:
      "Entre 19:00 e 21:00, o sistema faz outra varredura. Se ainda houver parcelas pendentes, enviamos mais um lembrete para ajudar o cliente a se organizar.",
    icon: "moon",
    variaveis: ["{nome_cliente}", "{valor_parcela}", "{data_vencimento}"],
    maxLen: 160,
  },
];

export function Step5Notifications({ state, setState }: Props) {
  const updateSlot = (
    key: NotificacaoKey,
    patch: Partial<{ ativo: boolean; texto: string }>,
  ) =>
    setState((prev) => ({
      ...prev,
      notificacoes: {
        ...prev.notificacoes,
        [key]: { ...prev.notificacoes[key], ...patch },
      },
    }));

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          5. Configuracoes de notificacoes do aplicativo
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Aqui voce define como as notificacoes padrao do sistema serao
          enviadas para o seu cliente no aplicativo.
        </p>
      </header>

      <InfoCallout>
        <strong>
          Estas mensagens são automáticas do sistema e são enviadas
          todos os dias para todos os clientes com plano ativo.
        </strong>
        <br />
        Nao e possivel desativar individualmente para cada cliente.
      </InfoCallout>

      {/* === Como funciona (toggle por slot) === */}
      <SectionCard
        icon={<InfoIcon />}
        title="Como funciona (padrao do sistema)"
        description="Todos os dias, o sistema envia lembretes automaticos em 3 momentos importantes para ajudar na organizacao."
      >
        <div className="flex flex-col gap-3">
          {SLOTS.map((slot) => (
            <SlotRow
              key={slot.key}
              slot={slot}
              ativo={state.notificacoes[slot.key]?.ativo ?? true}
              onToggle={(v) => updateSlot(slot.key, { ativo: v })}
            />
          ))}
        </div>
      </SectionCard>

      {/* === Mensagens editaveis === */}
      <SectionCard
        icon={<EditIcon />}
        title="Mensagens padrao do sistema (editaveis dentro do padrao)"
        description="Voce pode personalizar o texto das mensagens abaixo. Os horarios e o funcionamento nao podem ser alterados."
      >
        <div className="flex flex-col gap-6">
          {SLOTS.map((slot) => (
            <MessageEditor
              key={slot.key}
              slot={slot}
              texto={state.notificacoes[slot.key]?.texto ?? ""}
              onChange={(t) => updateSlot(slot.key, { texto: t })}
            />
          ))}
        </div>
      </SectionCard>

      <InfoCallout>
        As mensagens sao enviadas apenas pelo aplicativo do cliente e nao sao
        enviadas por WhatsApp ou e-mail.
      </InfoCallout>

      {/* === Resumo + Dica === */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_320px]">
        <SectionCard
          icon={<CheckIcon />}
          title="Resumo do funcionamento"
        >
          <ul className="flex flex-col gap-2 text-sm text-foreground">
            <SummaryRow>
              Todos os dias as <strong>09:00</strong> → lembrete informativo
              para todos os clientes.
            </SummaryRow>
            <SummaryRow>
              Todos os dias as <strong>17:00</strong> → verificacao de pendencias
              e envio de lembrete com detalhes.
            </SummaryRow>
            <SummaryRow>
              Todos os dias entre <strong>19:00 e 21:00</strong> → nova
              verificacao e envio de lembrete final.
            </SummaryRow>
            <SummaryRow>
              As notificacoes sao automaticas e ajudam a manter seus clientes
              organizados.
            </SummaryRow>
          </ul>
        </SectionCard>

        <div className="rounded-2xl border border-primary-200 bg-primary-50/40 p-5">
          <div className="flex items-center gap-2">
            <span className="text-base">⭐</span>
            <h3 className="text-sm font-bold text-primary-700">
              Dica importante
            </h3>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-primary-900/80">
            Essas notificacoes aumentam muito as chances de manter seus clientes
            em dia e evitar atrasos. E uma comunicacao amigavel e estrategica!
          </p>
        </div>
      </div>
    </div>
  );
}

// ======= Sub-componentes =======

function SlotRow({
  slot,
  ativo,
  onToggle,
}: {
  slot: SlotMeta;
  ativo: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-card p-4">
      <SlotIcon icon={slot.icon} />
      <div className="flex w-32 flex-shrink-0 flex-col">
        <span className="text-base font-bold text-foreground">{slot.horario}</span>
        <span className="text-xs text-muted-foreground">{slot.horarioLabel}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold text-foreground">{slot.titulo}</h4>
          <Badge tone={slot.badgeTone}>{slot.badge}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {slot.descricao}
        </p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <span
          className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
            ativo
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {ativo ? "Ativo" : "Inativo"}
        </span>
        <Toggle checked={ativo} onChange={onToggle} />
      </div>
    </div>
  );
}

function MessageEditor({
  slot,
  texto,
  onChange,
}: {
  slot: SlotMeta;
  texto: string;
  onChange: (v: string) => void;
}) {
  const len = texto.length;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[200px_1fr]">
      <div className="flex items-start gap-3">
        <SlotIcon icon={slot.icon} small />
        <div>
          <div className="text-sm font-bold text-foreground">{slot.horario}</div>
          <div className="text-xs text-muted-foreground">{slot.titulo}</div>
          <Badge tone={slot.badgeTone} className="mt-1">
            {slot.badge}
          </Badge>
        </div>
      </div>
      <div>
        <textarea
          value={texto}
          onChange={(e) =>
            onChange(e.target.value.slice(0, slot.maxLen))
          }
          rows={6}
          className="w-full rounded-xl border border-input bg-card p-3 text-sm leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="mt-1 flex items-start justify-between text-xs">
          <span className="text-muted-foreground">
            <span className="font-semibold">Variaveis disponiveis:</span>{" "}
            {slot.variaveis.join(", ")}
          </span>
          <span className="text-muted-foreground/80">
            {len}/{slot.maxLen}
          </span>
        </div>
      </div>
    </div>
  );
}

function SlotIcon({ icon, small }: { icon: SlotMeta["icon"]; small?: boolean }) {
  const size = small ? 36 : 56;
  const iconSize = small ? 18 : 24;
  const bg =
    icon === "sun"
      ? "bg-amber-50 text-amber-600"
      : icon === "clock"
        ? "bg-orange-50 text-orange-600"
        : "bg-primary-50 text-primary-600";
  return (
    <div
      className={`flex flex-shrink-0 items-center justify-center rounded-2xl ${bg}`}
      style={{ width: size, height: size }}
    >
      {icon === "sun" ? (
        <SunIcon size={iconSize} />
      ) : icon === "clock" ? (
        <ClockIcon size={iconSize} />
      ) : (
        <MoonIcon size={iconSize} />
      )}
    </div>
  );
}

function Badge({
  children,
  tone,
  className = "",
}: {
  children: React.ReactNode;
  tone: SlotMeta["badgeTone"];
  className?: string;
}) {
  const palette =
    tone === "info"
      ? "bg-sky-100 text-sky-700"
      : tone === "warning"
        ? "bg-amber-100 text-amber-700"
        : "bg-rose-100 text-rose-700";
  return (
    <span
      className={`inline-block rounded-md px-2 py-0.5 text-xs font-semibold ${palette} ${className}`}
    >
      {children}
    </span>
  );
}

function SummaryRow({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span className="text-sm leading-relaxed text-foreground">
        {children}
      </span>
    </li>
  );
}

// ======= Icones =======

function SunIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function ClockIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function MoonIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
